import { ReactNode } from "react";
import { StyleProp, TouchableOpacity, ViewStyle } from "react-native";

import { isReactText } from "../../utils";
import { useHover } from "../hooks";
import Text, { TextProps } from "../Text";
import View from "../View";
import Checkmark from "./Checkmark";

type Size = "small" | "medium" | "large";

export type CheckboxProps = {
  checked?: boolean;
  disabled?: boolean;
  error?: boolean;
  children?: ReactNode;
  /**
   * Defines size of the element
   * @default 'medium'
   */
  size?: Size;
  style?: StyleProp<ViewStyle>;
  onChange?(value: boolean): void;
  testID?: string;
};

const textVariantMap: Record<Size, TextProps["variant"]> = {
  small: "ui-small",
  medium: "ui-baseline",
  large: "ui-large",
};

export default function Checkbox(props: CheckboxProps) {
  const {
    checked,
    disabled,
    error,
    children,
    size = "medium",
    style,
    onChange,
    testID,
  } = props;

  const [hovered, hoverHandler] = useHover();

  function handleChange() {
    if (typeof onChange === "function") onChange(!checked);
  }

  return (
    <TouchableOpacity
      testID={['check_box', testID].filter(Boolean).join('-')}
      {...hoverHandler}
      activeOpacity={0.5}
      disabled={disabled}
      onPress={handleChange}
    >
      <View align="center" row spacing="xs" style={style}>
        <Checkmark
          checked={checked}
          disabled={disabled}
          error={error}
          hovered={hovered}
          size={size}
        />
        {isReactText(children) && (
          <Text
            ink={disabled ? "black-muted" : undefined}
            variant={textVariantMap[size]}
          >
            {children}
          </Text>
        )}
        {!isReactText(children) && children}
      </View>
    </TouchableOpacity>
  );
}
